import find from 'lodash/find'
import forEach from 'lodash/forEach'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import { providerKey } from 'v2/pages/settings/integrations/utils/provider-key'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { ActionConnectionLabel } from './action-connection-label'

/**
 * The single way to bind a saved action that carries `app_provider` but no
 * `org_app_uuid` to a live connection. Actions saved before the integrations-service
 * cutover still carry the old provider key ("google-mail"), so the match goes
 * through `providerKey` rather than raw equality.
 */
export const findActionConnectionForSavedProvider = (
  apps: RExternalOrgApp[] | undefined,
  savedProvider: string,
) => find(apps, (app) => providerKey(app.provider) === providerKey(savedProvider))

/** Connections as flat select options — the "pick a connection first" step. */
export const buildConnectionOptions = (
  apps: RExternalOrgApp[],
  accountLabels: Record<string, string>,
) =>
  map(apps, (app) => {
    const accountInfo = accountLabels[app.id]
    return {
      value: app.id,
      label: <ActionConnectionLabel app={app} accountInfo={accountInfo} />,
      // The label is a node, so the search text has to be carried separately.
      searchValue: `${app.name} ${accountInfo ?? ''}`.trim(),
      icon: app.logo_url ? (
        <img src={app.logo_url} alt={app.name} className="size-4 rounded" />
      ) : undefined,
    }
  })

/** The actions of one connection, keyed by the same encoded identifier the grouped list uses. */
export const buildActionOptionsForConnection = (
  app: RExternalOrgApp | undefined,
  encodeValue: (actionName: string, appId: string) => string,
) => {
  if (!app) return []
  return map(app.actions ?? [], (action) => ({
    label: action.description,
    value: encodeValue(action.action_name, app.id),
    searchValue: `${action.action_name} ${action.description}`,
  }))
}

export const buildActionConnectionAccountLabels = (
  apps: RExternalOrgApp[],
  userMap: Record<string, string>,
) => {
  const baseLabels = new Map(
    map(apps, (app): [string, string] => {
      const enrichedApp =
        app.integrated_by?.id && userMap[app.integrated_by.id]
          ? { ...app, integrated_by: { ...app.integrated_by, name: userMap[app.integrated_by.id] } }
          : app
      return [app.id, getAccountIdentifier(enrichedApp)]
    }),
  )
  const counts = new Map<string, number>()
  forEach(apps, (app) => {
    const key = `${app.name}|${baseLabels.get(app.id)}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })

  return fromPairs(
    map(apps, (app) => {
      let label = baseLabels.get(app.id) ?? ''
      if ((!label || (counts.get(`${app.name}|${label}`) ?? 0) > 1) && app.created_on) {
        const created = dayjs(app.created_on).format('MMM D, hh:mm A')
        label = label ? `${label} · ${created}` : created
      }
      return [app.id, label]
    }),
  )
}
